import {
  UpsellConfiguration,
  ProductUpsellColourPreset,
  ProductUpsellLayoutPreset,
  UpsellVariantMatch,
} from '../types/upsellTypes';
import { currentTheme } from '@/features/Page/data/globalData';
import { bfFreeGiftReplacedUpsellIds, bfFreeGiftUpsells } from './bfFreeGiftUpsellData';

const valueV34Strips = {
  upsellProduct: { product: 'V34_STRIPS_VALUE_DEAL' },
  preset: {
    colour: 'yellow' as ProductUpsellColourPreset,
  },
  upsellData: {
    content: {
      pillText: 'Stock Up & Save Even More',
      mainBody: {
        headerText: 'Add an extra V34 Whitening<br>Strips for just {{upsellPrice}}',
        descriptionText:
          '<highlight>Price usually {{comparePrice}}. Enjoy an<br>instant <bold>saving of {{percentageOff}}*</bold></highlight>',
      },
      buttonText: 'Add to Cart',
      termsAndConditionsText: '*Savings based on cost of singular box of V34 strips.',
    },
    style: {
      media: {
        mediaContainer: {
          alignItems: 'flex-end',
        },
      },
    },
  },
};

const cartValueV34Strips: UpsellConfiguration = {
  ...valueV34Strips,
  id: 'cart-value-v34-strips',
  displayRules: {
    excludedProductsInCart: {
      anyOf: [{ product: 'THE_STARTER_BUNDLE' }, { product: 'V34_STRIPS_VALUE_DEAL' }],
    },
    location: { include: ['/cart'] },
  },
  cartRules: {
    removeIfOnlyProductInCart: true,
  },
  actions: { add: [{ product: 'V34_STRIPS_VALUE_DEAL' }] },
  preset: {
    colour: 'yellow',
    layout: [{ preset: 'imageBottom', device: 'default' } as ProductUpsellLayoutPreset],
  },
  addAnotherTextSku: ['10060-VS'],
  upsellData: {
    content: {
      pillText: 'Stock Up & Save Even More',
      mainBody: {
        headerText: 'Add {{anotherText}} V34 Whitening<br>Strips for just {{upsellPrice}}',
        descriptionText:
          '<highlight>Price usually {{comparePrice}}. Enjoy an<br>instant <bold>saving of {{percentageOff}}*</bold></highlight>',
      },
      buttonText: 'Add to Cart',
      termsAndConditionsText: '*Savings based on cost of singular box of V34 strips.',
    },
  },
};

const productValueV34StripsStarterBundle: UpsellConfiguration = {
  ...valueV34Strips,
  id: 'product-value-v34-strips-starter-bundle',
  displayRules: {
    requiredProductsInStock: ['THE_STARTER_BUNDLE'],
    location: {
      include: ['/products/starter-bundle'],
    },
    excludedProductsInCart: {
      anyOf: [{ product: 'V34_STRIPS_VALUE_DEAL' }],
    },
  },
  actions: {
    add: [{ product: 'THE_STARTER_BUNDLE' }, { product: 'V34_STRIPS_VALUE_DEAL' }],
    redirect: '/cart',
  },
};

const productValueV34StripsAWS: UpsellConfiguration = {
  ...valueV34Strips,
  id: 'product-value-v34-strips-aws',
  displayRules: {
    requiredProductsInStock: ['AFFORDABLE_WHITENING_SET'],
    location: {
      include: ['/products/affordable-whitening-set'],
    },
    excludedProductsInCart: {
      anyOf: [{ product: 'V34_STRIPS_VALUE_DEAL' }],
    },
  },
  actions: {
    add: [{ product: 'AFFORDABLE_WHITENING_SET' }, { product: 'V34_STRIPS_VALUE_DEAL' }],
    redirect: '/cart',
  },
};

const productValueV34StripsPerfectPair: UpsellConfiguration = {
  ...valueV34Strips,
  id: 'product-value-v34-strips-perfect-pair',
  displayRules: {
    requiredProductsInStock: ['PERFECT_PAIR_SET'],
    location: {
      include: ['/products/perfect-pair-set'],
    },
    excludedProductsInCart: {
      anyOf: [{ product: 'V34_STRIPS_VALUE_DEAL' }],
    },
  },
  actions: {
    add: [{ product: 'PERFECT_PAIR_SET' }, { product: 'V34_STRIPS_VALUE_DEAL' }],
    redirect: '/cart',
  },
};

const productValueV34StripsMouthwashValueSet: UpsellConfiguration = {
  ...valueV34Strips,
  id: 'product-value-v34-strips-mouthwash-value-set',
  displayRules: {
    requiredProductsInStock: ['MOUTHWASH_VALUE_SET'],
    location: {
      include: ['/products/mouthwash-value-set'],
    },
    excludedProductsInCart: {
      anyOf: [{ product: 'V34_STRIPS_VALUE_DEAL' }],
    },
  },
  actions: {
    add: [{ product: 'MOUTHWASH_VALUE_SET' }, { product: 'V34_STRIPS_VALUE_DEAL' }],
    redirect: '/cart',
  },
};

const v34StripsValuePack = {
  upsellProduct: {
    product: `V34_STRIPS_VALUE_PACK`,
  },
  preset: {
    colour: 'green' as ProductUpsellColourPreset,
    shadow: { bottom: '0.5rem', width: '80%' },
    image: {
      mobile: { objectFit: 'contain' as const },
      desktop: { objectFit: 'cover' as const },
    },
  },
  upsellData: {
    content: {
      pillText: `Upgrade and Save`,
      mainBody: {
        headerText: `Get 2 for <highlight>{{upsellPrice}}</highlight>{{valuedAtPrice}}`,
        descriptionText: `Enjoy an instant <highlight><bold>saving of {{percentageOff}}</bold></highlight><br>when you buy two boxes in <br>this deal.`,
      },
      buttonText: `Add to Cart`,
      termsAndConditionsText: `Adding this offer will replace one V34 Strip box currently in your cart. *Discount based on full-price purchase of two boxes.`,
    },
    style: {
      mainBody: {
        comparePriceColor: 'highlight' as const,
      },
    },
  },
};

const productV34StripsValuePack: UpsellConfiguration = {
  ...v34StripsValuePack,
  id: 'product-v34-strips-value-pack',
  displayRules: {
    location: {
      include: ['/products/v34-whitening-strips'],
    },
    excludedProductsInCart: {
      anyOf: [{ product: 'V34_STRIPS_VALUE_PACK' }],
    },
  },
  actions: {
    add: [{ product: 'V34_STRIPS_VALUE_PACK' }],
    redirect: '/cart',
  },
};

const productSpanishV34StripsValuePack: UpsellConfiguration = {
  ...v34StripsValuePack,
  id: 'product-spanish-v34-strips-value-pack',
  displayRules: {
    location: {
      include: ['/products/tiras-blanqueadoras-v34'],
    },
    excludedProductsInCart: {
      anyOf: [{ product: 'V34_STRIPS_VALUE_PACK' }],
    },
  },
  actions: {
    add: [{ product: 'V34_STRIPS_VALUE_PACK' }],
    redirect: '/cart',
  },
  upsellData: {
    content: {
      pillText: `Mayor Valor`,
      mainBody: {
        headerText: `Compra 2 for <highlight>{{upsellPrice}}</highlight>`,
        descriptionText: `Compra dos cajas y <highlight><bold>ahorra el {{percentageOff}}</bold></highlight><br>instantaneamente.`,
      },
      showComparePrice: true,
      buttonText: `Compra y Ahorra`,
      termsAndConditionsText: `*Calculado sobre el precio normal de 2 cajas`,
    },
    style: {
      mainBody: {
        comparePriceColor: 'highlight' as const,
      },
    },
  },
};

const cartV34StripsValuePack: UpsellConfiguration = {
  ...v34StripsValuePack,
  id: 'cart-v34-strips-value-pack',
  displayRules: {
    requiredProductsInCart: {
      anyOf: [{ product: '10060-VS' }],
    },
    excludedProductsInCart: {
      anyOf: [{ product: 'V34_STRIPS_VALUE_PACK' }, { product: 'THE_STARTER_BUNDLE' }],
    },
    location: { include: ['/cart'] },
  },
  actions: {
    add: [{ product: 'V34_STRIPS_VALUE_PACK' }],
    remove: [{ product: '10060-VS', quantity: 1 }],
  },
};

const stainMouthwashValuePack = {
  upsellProduct: {
    product: `STAIN_MOUTHWASH_VALUE_PACK`,
  },
  preset: {
    colour: 'green' as ProductUpsellColourPreset,
    layout: [
      { preset: 'imageOverflow', device: 'mobile' } as ProductUpsellLayoutPreset,
      { preset: 'imageRight', device: 'mobile' } as ProductUpsellLayoutPreset,
      { preset: 'imageBottom', device: 'desktop' } as ProductUpsellLayoutPreset,
    ],
  },
  upsellData: {
    content: {
      pillText: `Upgrade and Save`,
      mainBody: {
        headerText: `Get 2 for <highlight>{{upsellPrice}}</highlight> {{valuedAtPrice}}`,
        descriptionText: `Enjoy an instant <highlight><bold>saving of over {{percentageOff}}*</bold></highlight><br> when you buy two bottles in <br>this bundle.`,
      },
      buttonText: `Add to Cart`,
      termsAndConditionsText: `Adding this offer will replace one iD Mouthwash item currently in your cart. *Discount based on full-price purchase of two bottles.`,
    },
  },
};

const productStainMouthwashValuePack: UpsellConfiguration = {
  ...stainMouthwashValuePack,
  id: 'product-stain-mouthwash-value-pack',
  displayRules: {
    excludedRegions: ['CA'],
    location: {
      include: ['/products/id-stain-mouthwash'],
    },
    excludedProductsInCart: {
      anyOf: [{ product: 'STAIN_MOUTHWASH_VALUE_PACK' }],
    },
  },
  actions: {
    add: [{ product: `STAIN_MOUTHWASH_VALUE_PACK` }],
    redirect: '/cart',
  },
};

const cartStainMouthwashValuePack: UpsellConfiguration = {
  ...stainMouthwashValuePack,
  id: 'cart-stain-mouthwash-value-pack',
  displayRules: {
    excludedRegions: ['CA'],
    requiredProductsInCart: {
      anyOf: [{ product: '10068-PO' }],
    },
    excludedProductsInCart: {
      anyOf: [{ product: `STAIN_MOUTHWASH_VALUE_PACK` }, { product: `THE_STARTER_BUNDLE` }],
    },
    location: {
      include: ['/cart'],
    },
  },
  actions: {
    add: [{ product: 'STAIN_MOUTHWASH_VALUE_PACK' }],
    remove: [{ product: '10068-PO', quantity: 1 }],
  },
};

const cartStainMouthwashValuePackTruFitToothbrush: UpsellConfiguration = {
  ...stainMouthwashValuePack,
  id: 'cart-stain-mouthwash-value-pack-tru-fit-toothbrush',
  displayRules: {
    excludedRegions: ['CA'],
    requiredProductsInCart: {
      anyOf: [{ product: '50001-TF-WH' }],
    },
    excludedProductsInCart: {
      anyOf: [{ product: 'STAIN_MOUTHWASH_VALUE_PACK' }, { product: 'THE_STARTER_BUNDLE' }],
    },
    location: {
      include: ['/cart'],
    },
  },
  actions: {
    add: [{ product: 'STAIN_MOUTHWASH_VALUE_PACK' }],
  },
  upsellData: {
    content: {
      ...stainMouthwashValuePack.upsellData.content,
      termsAndConditionsText: `*Discount based on full-price purchase of two bottles.`,
    },
  },
};

const cartStainMouthwashValuePackShipping: UpsellConfiguration = {
  ...stainMouthwashValuePack,
  id: 'cart-stain-mouthwash-value-pack-shipping',
  upsellProduct: {
    product: `STAIN_MOUTHWASH_VALUE_PACK_SHIPPING`,
  },
  preset: {
    ...stainMouthwashValuePack.preset,
  },
  displayRules: {
    includedRegions: ['AU', 'UK', 'US'],
    requiredProductsInCart: {
      anyOf: [{ product: '10068-PO8' }],
    },
    excludedProductsInCart: {
      anyOf: [{ product: `STAIN_MOUTHWASH_VALUE_PACK_SHIPPING` }],
    },
    location: {
      include: ['/cart'],
    },
  },
  cartRules: {
    requiredProductsInCart: {
      anyOf: [{ product: '10068-PO8' }],
    },
  },
  actions: {
    add: [{ product: 'STAIN_MOUTHWASH_VALUE_PACK_SHIPPING' }],
  },
  upsellData: {
    content: {
      pillText: `VIP Offer`,
      mainBody: {
        headerText: `Add 2 more for <highlight>{{upsellPrice}}</highlight> and<br>receive <highlight>FREE SHIPPING</highlight>`,
      },
      buttonText: `Add to Cart`,
      termsAndConditionsText: `Offer eligibility depends on qualifying items already in your cart. Terms apply.`,
    },
    style: {
      mainBody: {
        headerFontSize: { mobile: '1.25rem', desktop: '1.65rem' },
      },
    },
  },
};

const bargainMouthwash = {
  upsellProduct: { product: 'STAIN_MOUTHWASH_BARGAIN_PACK' },
  actions: { add: [{ product: 'STAIN_MOUTHWASH_BARGAIN_PACK' }] },
  preset: {
    colour: 'red' as ProductUpsellColourPreset,
    layout: { preset: 'imageBottom' } as ProductUpsellLayoutPreset,
  },
  addAnotherTextSku: ['10068-PO'],
  upsellData: {
    content: {
      pillText: '{{percentageOff}} Off Unlocked*',
      mainBody: {
        headerText: `Add {{anotherText}} iD Mouthwash<br>for only <highlight>{{upsellPrice}}</highlight> {{valuedAtPrice}}`,
      },
      buttonText: `Add to Cart`,
      termsAndConditionsText: `*Savings based on cost of singular bottle.`,
    },
  },
};

const cartBargainMouthwash: UpsellConfiguration = {
  ...bargainMouthwash,
  id: 'cart-bargain-mouthwash',
  displayRules: {
    excludedRegions: ['CA'],
    requiredProductsInCart: {
      anyOf: [
        { product: 'V34_STRIPS_VALUE_PACK' },
        { upsell: 'cart-tooth-armour-bargain-pack' },
        { upsell: 'cart-stain-mouthwash-value-pack' },
        { upsell: 'cart-stain-mouthwash-value-pack-tru-fit-toothbrush' },
        { upsell: 'product-stain-mouthwash-value-pack' },
        { product: 'STAIN_MOUTHWASH_BUNDLE_PACK' },
        { product: 'TOOTH_WIPES_BARGAIN_PACK' },
        { product: 'THE_STARTER_BUNDLE' },
        { product: 'WHITENING_PROTOCOL_BUNDLE' },
        { product: '10060-VS5' },
        { product: '50001-TF-WH5' },
      ],
    },
    excludedProductsInCart: {
      anyOf: [{ product: 'STAIN_MOUTHWASH_BARGAIN_PACK' }],
    },
    location: { include: ['/cart'] },
  },
  cartRules: {
    requiredProductsInCart: {
      anyOf: [
        { product: 'V34_STRIPS_VALUE_PACK' },
        { upsell: 'cart-tooth-armour-bargain-pack' },
        { upsell: 'cart-stain-mouthwash-value-pack' },
        { upsell: 'product-stain-mouthwash-value-pack' },
        { product: 'STAIN_MOUTHWASH_BUNDLE_PACK' },
        { product: 'TOOTH_WIPES_BARGAIN_PACK' },
        { product: 'THE_STARTER_BUNDLE' },
        { product: 'WHITENING_PROTOCOL_BUNDLE' },
        { product: '10060-VS5' },
        { upsell: 'cart-stain-mouthwash-value-pack-tru-fit-toothbrush' },
        { product: '50001-TF-WH5' },
      ],
    },
  },
  actions: { add: [{ product: 'STAIN_MOUTHWASH_BARGAIN_PACK' }] },
};

const cartBargainMouthwashUk: UpsellConfiguration = {
  ...bargainMouthwash,
  id: 'cart-bargain-mouthwash-uk',
  displayRules: {
    includedRegions: ['UK'],
    requiredProductsInCart: {
      anyOf: [{ product: `TOOTH_ARMOUR_VALUE_PACK` }, { product: 'TOOTH_ARMOUR_BFGS' }],
    },
    excludedProductsInCart: {
      anyOf: [{ product: 'STAIN_MOUTHWASH_BARGAIN_PACK' }],
    },
    location: { include: ['/cart'] },
  },
  cartRules: {
    requiredProductsInCart: {
      anyOf: [{ product: `TOOTH_ARMOUR_VALUE_PACK` }, { product: 'TOOTH_ARMOUR_BFGS' }],
    },
  },
  actions: { add: [{ product: 'STAIN_MOUTHWASH_BARGAIN_PACK' }] },
};

const cartBargainV34Strips: UpsellConfiguration = {
  id: 'cart-bargain-v34-strips',
  upsellProduct: { product: 'V34_STRIPS_BARGAIN_PACK' },
  addAnotherTextSku: ['10060-VS'],
  displayRules: {
    requiredProductsInCart: {
      anyOf: [
        { product: 'V34_STRIPS_BUNDLE_PACK' },
        { upsell: 'cart-bargain-mouthwash' },
        { product: 'THE_STARTER_BUNDLE' },
        { product: 'WHITENING_PROTOCOL_BUNDLE' },
        { product: '10060-VS5' },
      ],
    },
    excludedProductsInCart: {
      anyOf: [{ product: 'V34_STRIPS_BARGAIN_PACK' }],
    },
    location: { include: ['/cart'] },
  },
  cartRules: {
    requiredProductsInCart: {
      anyOf: [
        { product: 'V34_STRIPS_BUNDLE_PACK' },
        { upsell: 'cart-bargain-mouthwash' },
        { product: 'THE_STARTER_BUNDLE' },
        { product: 'WHITENING_PROTOCOL_BUNDLE' },
        { product: '10060-VS5' },
      ],
    },
  },
  actions: { add: [{ product: 'V34_STRIPS_BARGAIN_PACK' }] },
  preset: {
    colour: 'red',
    layout: [{ preset: 'imageBottom', device: 'default' } as ProductUpsellLayoutPreset],
    shadow: { bottom: '.5rem', width: '85%', marginBottom: '-0.5rem' },
    image: {
      mobile: { paddingRight: '0.5rem', marginBottom: '-0.85rem' },
      desktop: { paddingRight: '0.5rem' },
    },
  },
  upsellData: {
    content: {
      pillText: '{{percentageOff}} Off Unlocked*',
      mainBody: {
        headerText:
          'Add {{anotherText}} V34 Strips<br>for <highlight>{{upsellPrice}}</highlight> {{valuedAtPrice}}',
      },
      buttonText: 'Add to Cart',
      termsAndConditionsText: '*Savings based on cost of singular box of V34 Strips.',
    },
  },
};

const cartBargainV34StripsBulkPack: UpsellConfiguration = {
  ...cartBargainV34Strips,
  id: 'cart-bargain-v34-strips-bulk-pack',
  displayRules: {
    includedRegions: ['US', 'UK'],
    requiredProductsInCart: {
      anyOf: [{ product: '10068-PO8' }],
    },
    excludedProductsInCart: {
      anyOf: [{ product: 'V34_STRIPS_BARGAIN_PACK' }],
    },
    location: { include: ['/cart'] },
  },
  cartRules: {
    requiredProductsInCart: {
      anyOf: [{ product: '10068-PO8' }],
    },
  },
};

const productStarterBundle: UpsellConfiguration = {
  id: 'product-starter-bundle',
  upsellProduct: {
    product: `THE_STARTER_BUNDLE`,
  },
  choiceGroupSelection: {
    default: '90001-WM',
  },
  displayRules: {
    location: {
      include: ['/products'],
      exclude: [
        '/products/starter-bundle',
        '/products/id-stain-mouthwash',
        '/products/v34-whitening-strips',
        '/products/tooth-armour',
        '/products/exfoliating-tooth-wipes',
        '/products/tiras-blanqueadoras-v34',
        '/products/affordable-whitening-set',
        '/products/exfoliating-tooth-wipes',
        '/products/perfect-pair-set',
        '/products/mouthwash-value-set',
        '/products/electric-toothbrush',
        '/products/whitening-protocol-bundle',
        '/products/buy-two-get-three-free-v34-strips',
        '/products/buy-four-get-six-free-tooth-armour',
        '/products/buy-six-tooth-wipes-get-nine-free',
      ],
    },
    excludedProductsInCart: {
      anyOf: [{ product: 'THE_STARTER_BUNDLE' }],
    },
  },
  actions: {
    add: [{ product: 'THE_STARTER_BUNDLE' }],
    redirect: '/cart',
  },
  preset: {
    colour: 'yellow',
    layout: [
      { preset: 'imageOverflow', device: 'default' } as ProductUpsellLayoutPreset,
      { preset: 'slightRight', device: 'mobile' } as ProductUpsellLayoutPreset,
      { preset: 'imageLeft', device: 'desktop' } as ProductUpsellLayoutPreset,
    ],
  },
  upsellData: {
    content: {
      pillText: 'Bundle & Save',
      mainBody: {
        headerText: 'Add a Starter<br>Bundle for <highlight>{{upsellPrice}}</highlight>',
        descriptionText:
          '<highlight>A smarter way to start<br>whitening with better value</highlight>',
      },
      buttonText: `Add to Cart`,
      termsAndConditionsText: `*Savings based on non-discounted individual prices of items purchased separately. Toothpaste flavour as pictured.`,
    },
  },
};

const toothArmourValuePack = {
  upsellProduct: {
    product: `TOOTH_ARMOUR_VALUE_PACK`,
  },
  preset: {
    colour: 'pink' as ProductUpsellColourPreset,
    image: { mobile: { position: 'absolute', right: '0rem', bottom: '0' } },
  },
  upsellData: {
    content: {
      pillText: 'Upgrade and Save',
      mainBody: {
        headerText: `Get 2 for  <highlight>{{upsellPrice}}</highlight> {{valuedAtPrice}}`,
        descriptionText: `Enjoy an instant <highlight><bold>saving of {{percentageOff}}*</bold></highlight><br>when you buy two bottles in <br>this bundle.`,
      },
      buttonText: `Add to Cart`,
      termsAndConditionsText: `Adding this offer will replace one Tooth Armour item currently in your cart. *Discount based on full-price purchase of two bottles.`,
    },
  },
};

const productToothArmourValuePack: UpsellConfiguration = {
  ...toothArmourValuePack,
  id: 'product-tooth-armour-value-pack',
  displayRules: {
    location: {
      include: ['/products/tooth-armour'],
    },
    excludedProductsInCart: {
      anyOf: [{ product: 'TOOTH_ARMOUR_VALUE_PACK' }],
    },
  },
  actions: {
    add: [{ product: 'TOOTH_ARMOUR_VALUE_PACK' }],
    redirect: '/cart',
  },
};

const cartToothArmourValuePack: UpsellConfiguration = {
  ...toothArmourValuePack,
  id: 'cart-tooth-armour-value-pack',
  displayRules: {
    excludedRegions: ['UK'],
    requiredProductsInCart: {
      anyOf: [{ product: '10065-TA' }],
    },
    excludedProductsInCart: {
      anyOf: [{ product: `TOOTH_ARMOUR_VALUE_PACK` }],
    },
    location: {
      include: ['/cart'],
    },
  },
  actions: {
    add: [{ product: 'TOOTH_ARMOUR_VALUE_PACK' }],
    remove: [{ product: '10065-TA', quantity: 1 }],
  },
};

const cartToothArmourValuePackUk: UpsellConfiguration = {
  ...toothArmourValuePack,
  id: 'cart-tooth-armour-value-pack-uk',
  displayRules: {
    includedRegions: ['UK'],
    requiredProductsInCart: {
      anyOf: [{ product: '10065-TA' }],
    },
    excludedProductsInCart: {
      anyOf: [{ product: `TOOTH_ARMOUR_VALUE_PACK` }],
    },
    location: {
      include: ['/cart'],
    },
  },
  actions: {
    add: [{ product: 'TOOTH_ARMOUR_VALUE_PACK' }],
    remove: [{ product: '10065-TA', quantity: 1 }],
  },
};

const cartToothArmourValuePackShipping: UpsellConfiguration = {
  ...toothArmourValuePack,
  id: 'cart-tooth-armour-value-pack-bfgs',
  upsellProduct: {
    product: `TOOTH_ARMOUR_VALUE_PACK_SHIPPING`,
  },
  displayRules: {
    includedRegions: ['AU', 'UK'],
    requiredProductsInCart: {
      anyOf: [{ product: 'TOOTH_ARMOUR_BFGS' }],
    },
    excludedProductsInCart: {
      anyOf: [{ product: `TOOTH_ARMOUR_VALUE_PACK_SHIPPING` }],
    },
    location: {
      include: ['/cart'],
    },
  },
  cartRules: {
    requiredProductsInCart: {
      anyOf: [{ product: 'TOOTH_ARMOUR_BFGS' }],
    },
  },
  actions: {
    add: [{ product: 'TOOTH_ARMOUR_VALUE_PACK_SHIPPING' }],
  },
  upsellData: {
    content: {
      pillText: `VIP Offer`,
      mainBody: {
        headerText: `Add 2 more for <highlight>{{upsellPrice}}</highlight> <br>and receive<br> <highlight>FREE SHIPPING</highlight>`,
      },
      buttonText: `Add to Cart`,
      termsAndConditionsText: `Offer eligibility depends on qualifying items already in your cart. Terms apply.`,
    },
  },
};

const toothArmourBargainPack = {
  upsellProduct: { product: 'TOOTH_ARMOUR_BARGAIN_PACK' },
  preset: {
    colour: 'red' as ProductUpsellColourPreset,
    shadow: { bottom: '0.5rem' },
    image: { padding: '0.5rem' },
  },
  upsellData: {
    content: {
      pillText: '{{percentageOff}} Off Unlocked*',
      mainBody: {
        headerText:
          'Add Tooth Armour<br>for only <highlight>{{upsellPrice}}</highlight>{{valuedAtPrice}}',
      },
      buttonText: 'Add to Cart',
      termsAndConditionsText: '*Savings based on cost of singular bottle.',
    },
  },
};

const cartToothArmourBargainPack: UpsellConfiguration = {
  ...toothArmourBargainPack,
  id: 'cart-tooth-armour-bargain-pack',
  displayRules: {
    excludedRegions: ['UK'],
    requiredProductsInCart: {
      anyOf: [
        { upsell: 'cart-tooth-armour-value-pack' },
        { upsell: 'product-tooth-armour-value-pack' },
        { product: 'TOOTH_ARMOUR_BUNDLE_PACK' },
        { product: 'TOOTH_ARMOUR_BFGS' },
      ],
    },
    excludedProductsInCart: {
      anyOf: [{ product: 'TOOTH_ARMOUR_BARGAIN_PACK' }],
    },
    location: { include: ['/cart'] },
  },
  cartRules: {
    requiredProductsInCart: {
      anyOf: [
        { upsell: 'cart-tooth-armour-value-pack' },
        { upsell: 'product-tooth-armour-value-pack' },
        { product: 'TOOTH_ARMOUR_BUNDLE_PACK' },
        { product: 'TOOTH_ARMOUR_BFGS' },
      ],
    },
  },
  actions: { add: [{ product: 'TOOTH_ARMOUR_BARGAIN_PACK' }] },
};

const cartToothArmourBargainPackUk: UpsellConfiguration = {
  ...toothArmourBargainPack,
  id: 'cart-tooth-armour-bargain-pack-uk',
  displayRules: {
    includedRegions: ['UK'],
    requiredProductsInCart: {
      anyOf: [{ upsell: 'cart-bargain-mouthwash-uk' }, { product: 'TOOTH_ARMOUR_BUNDLE_PACK' }],
    },
    excludedProductsInCart: {
      anyOf: [{ product: `TOOTH_ARMOUR_BARGAIN_PACK` }],
    },
    location: {
      include: ['/cart'],
    },
  },
  cartRules: {
    requiredProductsInCart: {
      anyOf: [{ upsell: 'cart-bargain-mouthwash-uk' }, { product: 'TOOTH_ARMOUR_BUNDLE_PACK' }],
    },
  },
  actions: {
    add: [{ product: 'TOOTH_ARMOUR_BARGAIN_PACK' }],
  },
};

const toothWipesValuePack = {
  upsellProduct: {
    product: `TOOTH_WIPES_VALUE_PACK`,
  },
  preset: {
    colour: 'blue' as ProductUpsellColourPreset,
    image: {
      mobile: { position: 'absolute', right: 0, bottom: 0, width: '80%' },
      desktop: { position: 'absolute', right: 0, bottom: 0, width: '90%', smallWidth: '80%' },
    },
  },
  upsellData: {
    content: {
      pillText: `Upgrade and Save`,
      mainBody: {
        headerText: `Get 2 for <highlight>{{upsellPrice}}</highlight> {{valuedAtPrice}}`,
        descriptionText: `Enjoy an instant <highlight><bold>saving of over {{percentageOff}}*<br></bold></highlight> when you buy two packs in <br>this bundle.`,
      },
      buttonText: `Add to Cart`,
      termsAndConditionsText: `Adding this offer will replace one Tooth Wipe item currently in your cart. *Discount based on full-price purchase of two packs.`,
    },
  },
};

const productToothWipesValuePack: UpsellConfiguration = {
  ...toothWipesValuePack,
  id: 'product-tooth-wipes-value-pack',
  displayRules: {
    excludedRegions: ['CA'],
    location: {
      include: ['/products/exfoliating-tooth-wipes'],
    },
    excludedProductsInCart: {
      anyOf: [{ product: `TOOTH_WIPES_VALUE_PACK` }],
    },
  },
  actions: {
    add: [{ product: `TOOTH_WIPES_VALUE_PACK` }],
    redirect: '/cart',
  },
};

const cartToothWipesValuePack: UpsellConfiguration = {
  ...toothWipesValuePack,
  id: 'cart-tooth-wipes-value-pack',
  displayRules: {
    excludedRegions: ['CA'],
    requiredProductsInCart: {
      anyOf: [{ product: '10069-TW' }],
    },
    excludedProductsInCart: {
      anyOf: [{ product: `TOOTH_WIPES_VALUE_PACK` }],
    },
    location: {
      include: ['/cart'],
    },
  },
  actions: {
    add: [{ product: `TOOTH_WIPES_VALUE_PACK` }],
    remove: [{ product: '10069-TW', quantity: 1 }],
  },
};

const toothWipesBargainPack = {
  upsellProduct: { product: 'TOOTH_WIPES_BARGAIN_PACK' },
  preset: {
    colour: 'red' as ProductUpsellColourPreset,
  },
  upsellData: {
    content: {
      pillText: '{{percentageOff}} Unlocked*',
      mainBody: {
        headerText:
          'Add Tooth Wipes<br>for only <highlight>{{upsellPrice}}</highlight>{{valuedAtPrice}}</span>',
      },
      buttonText: 'Add to Cart',
      termsAndConditionsText: '*Savings based on cost of singular pack.',
    },
  },
};

const cartToothWipesBargainPack: UpsellConfiguration = {
  ...toothWipesBargainPack,
  id: 'cart-tooth-wipes-bargain-pack',
  displayRules: {
    excludedRegions: ['CA'],
    requiredProductsInCart: {
      anyOf: [{ product: 'TOOTH_WIPES_VALUE_PACK' }, { product: '10069-TW15' }],
    },
    excludedProductsInCart: {
      anyOf: [{ product: 'TOOTH_WIPES_BARGAIN_PACK' }],
    },
    location: { include: ['/cart'] },
  },
  cartRules: {
    requiredProductsInCart: {
      anyOf: [{ product: 'TOOTH_WIPES_VALUE_PACK' }, { product: '10069-TW15' }],
    },
  },
  actions: { add: [{ product: 'TOOTH_WIPES_BARGAIN_PACK' }] },
};

const electricToothbrushSkus = [
  '41001-ET-PI',
  '41002-ET-HP',
  '41003-ET-GR',
  '41004-ET-PU',
  '41008-ET-OR',
  '41009-ET-YE',
  '41010-ET-GG',
  '41011-ET-EB',
  '41017-ET-CR',
  '41018-ET-BB',
  '41022-ET-KP',
  '41023-ET-BL',
  '41024-ET-BM',
  '41026-ET-SO',
  '41027-ET-MI',
  '41029-ET-MM',
];

const etbVariantMatch: UpsellVariantMatch = {
  from: 'triggerVariant',
  skus: electricToothbrushSkus,
  quantity: 2,
  fallback: { default: '41023-ET-BL', us: '41004-ET-PU', eu: '41004-ET-PU' },
};

const etbValuePack = {
  upsellProduct: {
    product: `ETB_VALUE_PACK`,
  },
  preset: {
    colour: 'green' as ProductUpsellColourPreset,
    layout: [{ preset: 'imageBottom', device: 'default' } as ProductUpsellLayoutPreset],
    image: { paddingLeft: '1rem' },
    shadow: { bottom: '0rem' },
  },
  upsellData: {
    content: {
      pillText: `Upgrade and Save`,
      mainBody: {
        headerText: `Or Get 2 for <highlight>{{upsellPrice}}</highlight> {{valuedAtPrice}}`,
        descriptionText: `Enjoy an instant <highlight><bold>saving of {{percentageOff}}*</bold></highlight><br>when you buy two in this bundle.`,
      },
      buttonText: `Add to Cart`,
      termsAndConditionsText: `Adding this offer will replace all Electric Toothbrushes currently in your cart with 2x {{variantColour}} Electric Toothbrushes. *Discount based on full-price purchase of two Electric Toothbrushes.`,
    },
    style: {
      mainBody: {
        comparePriceColor: 'highlight' as const,
      },
    },
  },
};

const productEtbValuePackBlack: UpsellConfiguration = {
  ...etbValuePack,
  id: 'product-etb-value-pack',
  choiceGroupSelection: etbVariantMatch,
  displayRules: {
    excludedRegions: ['US', 'EU'],
    location: {
      include: ['/products/electric-toothbrush'],
    },
    excludedProductsInCart: {
      anyOf: [{ product: 'ETB_VALUE_PACK' }, { product: 'ULTIMATE_BUNDLE' }],
    },
  },
  actions: {
    add: [{ product: 'ETB_VALUE_PACK' }],
    redirect: '/cart',
  },
};

const cartEtbValuePackBlack: UpsellConfiguration = {
  ...etbValuePack,
  id: 'cart-etb-value-pack',
  choiceGroupSelection: etbVariantMatch,
  displayRules: {
    excludedRegions: ['US', 'EU'],
    requiredProductsInCart: {
      anyOf: electricToothbrushSkus.map((product) => ({ product })),
    },
    excludedProductsInCart: {
      anyOf: [{ product: 'ETB_VALUE_PACK' }, { product: 'ULTIMATE_BUNDLE' }],
    },
    location: { include: ['/cart'] },
  },
  actions: {
    add: [{ product: 'ETB_VALUE_PACK' }],
    remove: electricToothbrushSkus.map((product) => ({ product })),
  },
};

const productEtbValuePackPurple: UpsellConfiguration = {
  ...etbValuePack,
  id: 'product-etb-value-pack-purple',
  choiceGroupSelection: etbVariantMatch,
  displayRules: {
    excludedRegions: ['INT', 'AU', 'UK', 'CA'],
    location: {
      include: ['/products/electric-toothbrush'],
    },
    excludedProductsInCart: {
      anyOf: [{ product: 'ETB_VALUE_PACK' }, { product: 'ULTIMATE_BUNDLE' }],
    },
  },
  actions: {
    add: [{ product: 'ETB_VALUE_PACK' }],
    redirect: '/cart',
  },
};

const cartEtbValuePackPurple: UpsellConfiguration = {
  ...etbValuePack,
  id: 'cart-etb-value-pack-purple',
  choiceGroupSelection: etbVariantMatch,
  displayRules: {
    excludedRegions: ['INT', 'AU', 'UK', 'CA'],
    requiredProductsInCart: {
      anyOf: electricToothbrushSkus.map((product) => ({ product })),
    },
    excludedProductsInCart: {
      anyOf: [{ product: 'ETB_VALUE_PACK' }, { product: 'ULTIMATE_BUNDLE' }],
    },
    location: { include: ['/cart'] },
  },
  actions: {
    add: [{ product: 'ETB_VALUE_PACK' }],
    remove: electricToothbrushSkus.map((product) => ({ product })),
  },
};

const cartBargainPapStrips = {
  id: 'cart-pap-strips-bargain-pack',
  upsellProduct: { product: 'PAP_STRIPS_BARGAIN_PACK' },
  actions: { add: [{ product: 'PAP_STRIPS_BARGAIN_PACK' }] },
  preset: {
    colour: 'green' as ProductUpsellColourPreset,
    layout: { preset: 'imageOverflow' } as ProductUpsellLayoutPreset,
  },
  upsellData: {
    content: {
      pillText: '{{percentageOff}} Off Unlocked*',
      mainBody: {
        headerText: `Add PAP+ Strips<br>for only <highlight>{{upsellPrice}}</highlight> {{valuedAtPrice}}`,
      },
      buttonText: `Add to Cart`,
      termsAndConditionsText: `*Discount based on full-price purchase of product.`,
    },
  },
  displayRules: {
    includedRegions: ['AU'],
    requiredProductsInCart: {
      anyOf: [{ product: 'LIP_BALM_BUNDLE' }],
    },
    excludedProductsInCart: {
      anyOf: [{ product: 'PAP_STRIPS_BARGAIN_PACK' }],
    },
    location: { include: ['/cart'] },
  },
  cartRules: {
    requiredProductsInCart: {
      anyOf: [{ product: 'LIP_BALM_BUNDLE' }],
    },
  },
};

const standardUpsells: UpsellConfiguration[] = [
  productToothArmourValuePack,
  productV34StripsValuePack,
  productSpanishV34StripsValuePack,
  productStainMouthwashValuePack,
  productValueV34StripsStarterBundle,
  productValueV34StripsAWS,
  productToothWipesValuePack,
  productEtbValuePackBlack,
  productEtbValuePackPurple,
  productValueV34StripsPerfectPair,
  productValueV34StripsMouthwashValueSet,
  productStarterBundle,
  cartStainMouthwashValuePackShipping,
  cartToothArmourValuePackShipping,
  cartToothArmourBargainPack,
  cartToothArmourBargainPackUk,
  cartBargainMouthwash,
  cartBargainMouthwashUk,
  cartBargainV34Strips,
  cartBargainV34StripsBulkPack,
  cartStainMouthwashValuePack,
  cartStainMouthwashValuePackTruFitToothbrush,
  cartToothArmourValuePack,
  cartToothArmourValuePackUk,
  cartToothWipesValuePack,
  cartEtbValuePackBlack,
  cartEtbValuePackPurple,
  cartToothWipesBargainPack,
  cartV34StripsValuePack,
  cartBargainPapStrips,
  cartValueV34Strips,
];

export const allUpsells: UpsellConfiguration[] =
  currentTheme === 'bfFreeGiftTheme'
    ? [
        ...bfFreeGiftUpsells,
        ...standardUpsells.filter(({ id }) => !bfFreeGiftReplacedUpsellIds.includes(id)),
      ]
    : standardUpsells;
